// src/merch/FullscreenEditor.jsx
//
// Full-screen flat editor, built for phones (works on any screen). The
// sticker fills the viewport; a bottom toolbar switches between Move
// (select / drag / handles / pinch) and Draw.
//
// Switching Draw → Move (or tapping Done) turns the drawing into a layer,
// so kids never end up with a drawing they can't move.

import React, { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { openTextDialog } from "../engine";
import { merch, removeLayer, moveLayer, updateTransform, toggleFlip, duplicateLayer } from "./store";
import { switchSide } from "./sides";
import { commitPaintAsLayer } from "./paint";
import PrintAreaEditor from "./PrintAreaEditor.jsx";
import PaintTools from "./PaintTools.jsx";

function usePortrait() {
  const get = () => typeof window !== "undefined" && window.innerHeight > window.innerWidth;
  const [p, setP] = useState(get);
  useEffect(() => {
    const on = () => setP(get());
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    return () => {
      window.removeEventListener("resize", on);
      window.removeEventListener("orientationchange", on);
    };
  }, []);
  return p;
}

export default function FullscreenEditor({ product, size, colour, area, sticker, composite, sides = [], onAddImage, onClose }) {
  const snap = useSnapshot(merch);
  const portrait = usePortrait();
  const drawing = snap.paint.active;
  const sel = snap.layers.find((l) => l.id === snap.selectedId && !l.locked) || null;
  const selIndex = sel ? snap.layers.indexOf(sel) : -1;
  const wide = sticker.widthMm / sticker.heightMm > 1.8;

  // lock page scroll behind the overlay
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  function commitIfDrawing() {
    if (merch.paint.strokes) commitPaintAsLayer();
  }
  function setMode(draw) {
    if (!draw) commitIfDrawing();
    merch.paint.active = draw;
  }
  function done() {
    commitIfDrawing();
    merch.paint.active = false;
    onClose();
  }

  return (
    <div className="ma-fs" role="dialog" aria-label="Full-screen editor">
      <header className="ma-fs-bar">
        <button className="ma-btn primary" onClick={done}>Done</button>
        {sides.length > 1 ? (
          <div className="ma-fs-title">
            <div className="ma-seg small">
              {sides.map((s) => (
                <button key={s.id} aria-pressed={s.id === snap.side} onClick={() => switchSide(s.id)}>{s.label}</button>
              ))}
            </div>
          </div>
        ) : (
          <div className="ma-fs-title">{product.name} · {size.label}</div>
        )}
        <div className="ma-seg" role="tablist">
          <button role="tab" aria-pressed={!drawing} onClick={() => setMode(false)}>Move</button>
          <button role="tab" aria-pressed={drawing} onClick={() => setMode(true)}>Draw</button>
        </div>
      </header>

      <PrintAreaEditor
        key={snap.side}
        guides={sides.find((z) => z.id === snap.side)?.guides}
        fill
        area={area}
        sticker={sticker}
        colour={colour}
        composite={composite}
        selectedId={snap.selectedId}
        paintActive={drawing}
      />

      {portrait && wide && (
        <div className="ma-fs-hint">↻ Turn your phone sideways for a bigger sticker</div>
      )}

      <footer className="ma-fs-tools">
        {drawing ? (
          <PaintTools compact />
        ) : (
          <div className="ma-fs-row">
            <button className="ma-btn" onClick={onAddImage}>+ Image</button>
            <button className="ma-btn" onClick={() => openTextDialog()}>+ Text</button>
            {sel ? (
              <>
                {sel.kind === "text" && (
                  <button className="ma-btn ghost" onClick={() => openTextDialog(sel.id)}>Edit</button>
                )}
                <button className="ma-btn ghost" title="Bring forward" disabled={selIndex <= 0} onClick={() => moveLayer(sel.id, "up")}>Forward</button>
                <button className="ma-btn ghost" title="Send back" disabled={selIndex >= snap.layers.length - 1} onClick={() => moveLayer(sel.id, "down")}>Back</button>
                <button className="ma-btn ghost" aria-pressed={!!sel.transform?.flipX} onClick={() => toggleFlip(sel.id)}>⇋ Flip</button>
                <button className="ma-btn ghost" onClick={() => updateTransform(sel.id, { tx: 0.5, ty: 0.5 })}>Centre</button>
                <button className="ma-btn ghost" onClick={() => duplicateLayer(sel.id)}>Duplicate</button>
                <button className="ma-btn ghost danger" onClick={() => removeLayer(sel.id)}>Delete</button>
              </>
            ) : (
              <span className="ma-muted">Tap something to move it · pinch to resize</span>
            )}
          </div>
        )}
      </footer>
    </div>
  );
}
